import React, { useEffect, useState, useRef } from 'react';
import { Upload, Trash2, FileText, AlertCircle } from 'lucide-react';

const MultiFileUpload = ({ 
  onFilesChange, 
  maxSizeMB = 10, 
  allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'],
  value,
  multiple = true,
  maxFiles,
  disabled = false,
  helperText = '',
  uploading = false,
  externalError = '',
  renderFileDetails
}) => {
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const displayedFiles = value !== undefined ? value : files;

  useEffect(() => {
    if (value === undefined) return;
    setFiles(value);
  }, [value]);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    processFiles(selectedFiles);
    e.target.value = '';
  };

  const processFiles = (newFiles) => {
    setError(null);
    if (disabled) return;

    const normalizedFiles = multiple ? newFiles : newFiles.slice(0, 1);
    const nextFiles = multiple ? [...displayedFiles, ...normalizedFiles] : normalizedFiles;
    const resolvedMaxFiles = maxFiles || (multiple ? null : 1);

    if (resolvedMaxFiles && nextFiles.length > resolvedMaxFiles) {
      setError(`You can upload up to ${resolvedMaxFiles} file${resolvedMaxFiles > 1 ? 's' : ''}.`);
      return;
    }
    
    const totalSize = nextFiles.reduce((acc, f) => acc + f.size, 0);
    if (totalSize > maxSizeMB * 1024 * 1024) {
      setError(`Total file size exceeds ${maxSizeMB}MB limit.`);
      return;
    }

    const invalidFiles = normalizedFiles.filter(f => !allowedTypes.includes(f.type));
    if (invalidFiles.length > 0) {
      setError("Some files have unsupported formats (Allowed: JPG, PNG, PDF).");
      return;
    }

    setFiles(nextFiles);
    onFilesChange(nextFiles);
  };

  const removeFile = (index) => {
    if (disabled) return;
    const updatedFiles = displayedFiles.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    onFilesChange(updatedFiles);
  };

  const formatSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const activeError = error || externalError;

  return (
    <div style={containerStyle}>
      <div 
        style={{ 
          ...dropZoneStyle,
          ...(activeError ? errorDropZoneStyle : {}),
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.7 : 1
        }}
        onClick={() => !disabled && fileInputRef.current.click()}
        onDragOver={(e) => { if (disabled) return; e.preventDefault(); e.currentTarget.style.borderColor = '#2563eb'; }}
        onDragLeave={(e) => { if (disabled) return; e.preventDefault(); e.currentTarget.style.borderColor = '#cbd5e1'; }}
        onDrop={(e) => {
          if (disabled) return;
          e.preventDefault();
          e.currentTarget.style.borderColor = '#cbd5e1';
          processFiles(Array.from(e.dataTransfer.files));
        }}
      >
        <Upload size={32} color="#64748b" />
        <div style={{ marginTop: '10px', textAlign: 'center' }}>
          <p style={{ margin: 0, fontWeight: '700', color: '#1e293b' }}>
            {multiple ? 'Click or drag files to upload' : 'Click or drag a file to upload'}
          </p>
          <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#64748b' }}>
            Supported: JPG, PNG, PDF (Max Total: {maxSizeMB}MB)
          </p>
          {helperText && <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#64748b' }}>{helperText}</p>}
        </div>
        <input 
          type="file" 
          multiple={multiple}
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
          accept={allowedTypes.join(',')}
          disabled={disabled}
        />
      </div>

      {activeError && (
        <div style={errorStyle}>
          <AlertCircle size={16} />
          <span>{activeError}</span>
        </div>
      )}

      {displayedFiles.length > 0 && (
        <div style={fileListStyle}>
          {displayedFiles.map((file, index) => (
            <div key={index} style={fileItemStyle}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', flex: 1, minWidth: 0, overflow: 'hidden' }}>
                <div style={{ paddingTop: '2px' }}>
                  <FileText size={20} color="#2563eb" />
                </div>
                <div style={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
                  <p style={fileNameStyle} title={file.name}>{file.name}</p>
                  <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>{file.size ? formatSize(file.size) : 'Saved file'}</p>
                  {renderFileDetails && (
                    <div style={{ marginTop: '10px' }}>
                      {renderFileDetails(file, index)}
                    </div>
                  )}
                </div>
              </div>
              <button 
                onClick={(e) => { e.stopPropagation(); removeFile(index); }}
                style={{ ...removeBtnStyle, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }}
                disabled={disabled}
              >
                <Trash2 size={16} color="#dc2626" />
              </button>
            </div>
          ))}
        </div>
      )}

      {uploading && (
        <div style={progressWrapStyle}>
          <div style={progressTrackStyle}>
            <div style={progressBarStyle} />
          </div>
        </div>
      )}
    </div>
  );
};

const containerStyle = { width: '100%', minWidth: 0 };
const dropZoneStyle = { border: '2px dashed #cbd5e1', borderRadius: '12px', padding: '30px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', backgroundColor: '#f8fafc', transition: 'all 0.2s ease' };
const errorDropZoneStyle = { borderColor: '#dc2626', backgroundColor: '#fef2f2' };
const fileListStyle = { marginTop: '20px', display: 'grid', gridTemplateColumns: '1fr', gap: '8px', width: '100%', minWidth: 0 };
const fileItemStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', minWidth: 0, boxSizing: 'border-box', padding: '10px 15px', backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px' };
const fileNameStyle = { margin: 0, fontSize: '13px', fontWeight: '600', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const removeBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '4px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const errorStyle = { marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px', color: '#ef4444', fontSize: '13px', backgroundColor: '#fef2f2', padding: '8px 12px', borderRadius: '6px' };
const progressWrapStyle = { marginTop: '12px' };
const progressTrackStyle = { width: '100%', height: '6px', backgroundColor: '#e2e8f0', borderRadius: '999px', overflow: 'hidden' };
const progressBarStyle = { width: '45%', height: '100%', backgroundColor: '#2563eb', borderRadius: '999px', animation: 'multi-upload-progress 1.2s ease-in-out infinite' };

if (typeof document !== 'undefined' && !document.getElementById('multi-upload-progress-style')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'multi-upload-progress-style';
  styleSheet.innerHTML = `
    @keyframes multi-upload-progress {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(320%); }
    }
  `;
  document.head.appendChild(styleSheet);
}

export default MultiFileUpload;
